import { useSyncExternalStore, type ComponentType } from 'react';
import type { BoardView, SelfInfo, ServerEventMessage } from '@momentum/shared';

/**
 * Props for an optional 3D board renderer. Kept minimal and framework-agnostic (no three.js types leak here)
 * so this file — and everything that imports it — stays free of a `three` dependency. The real implementation
 * lives in `packages/client/src/renderers/board3d.tsx`, dynamically imported so it never loads on the 2D path.
 */
export interface Board3DProps {
  view: BoardView;
  you: SelfInfo | null;
  /** Subscribe to this instance's ephemeral events (`board.move`, `board.dice`). Returns an unsubscribe function. */
  onEvent: (listener: (event: ServerEventMessage) => void) => () => void;
  reducedMotion: boolean;
  /** Local animation can defer controls; it never advances authoritative game state. */
  onBusyChange?: (busy: boolean) => void;
  /** Same authorised action used by the accessible Roll control. */
  onRoll?: () => void;
  rollEnabled?: boolean;
  /**
   * Same authorised action as the accessible route control, addressed by destination space id.
   * The 3D board presents the choice as in-world chevrons (04-mobile-camera-views.png panel 02);
   * the panel keeps the real buttons for screen readers and keyboards.
   */
  onRoute?: (to: string) => void;
}

export type Board3DComponent = ComponentType<Board3DProps>;

/**
 * Module-level registration slot for a 3D board renderer. `packages/modules/src/board/BoardRenderer.tsx` reads
 * it through `useBoard3D()` and only offers the "3D" toggle once something has registered. The client app calls
 * `registerBoard3D` once at startup (see `packages/client/src/renderers/index.ts`'s `setupRenderers()`); nothing
 * in the `@momentum/modules` package ever imports `three` or the client's renderer file directly.
 */
let current: Board3DComponent | null = null;
const listeners = new Set<() => void>();

export function registerBoard3D(component: Board3DComponent): void {
  current = component;
  for (const listener of listeners) listener();
}

/** Test/debug helper: clears the registration (e.g. between test cases). */
export function unregisterBoard3D(): void {
  current = null;
  for (const listener of listeners) listener();
}

export function getBoard3D(): Board3DComponent | null {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reactive read of the registered 3D renderer, or `null` if none has registered (yet). */
export function useBoard3D(): Board3DComponent | null {
  return useSyncExternalStore(subscribe, getBoard3D, () => null);
}
